// Libs
import React, { useEffect, useState } from 'react';
import { Tooltip, Modal } from 'antd';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import * as Yup from 'yup';

// Own Libs
import { Button, FormDatePicker, FormSelect, FormSubmitButton, MasterDetailForm, Row } from '@digital/ui';

//Api
import { anexarRelatorioConfide, ISasPropostaOrdemCompra, ISasPropostaOrdemCompraDocs } from '@app/api/sas/sasProposta';

//Files
import { editandoState, modalAcoesIdState, vendorListDocsState } from '../atoms';
import { UploadFile, Visibility } from '@app/components';

//Utils
import { isObjNotEmpty, vtObjToFormData } from '@app/helpers/objHelper';
import { EStatusRelatorioConfide } from '@app/enums/EResultadoEnvioConfide';
import ModalConfirmContentJustificativa from '@app/components/ModalConfirmContent/ModalConfirmContentJustificativa';
import { IArquivo } from '@app/types/core.types';

interface Props {
    sasProposta: ISasPropostaOrdemCompra;
}

export const ModalStatusRelatorioConfide = ({ sasProposta }: Props) => {

    const setModalAcoesId = useSetRecoilState(modalAcoesIdState);
    const editando = useRecoilValue(editandoState);

    const vendorListDocs = useRecoilValue(vendorListDocsState);

    const [exibirModal, setExibirModal] = useState(false);
    const [doc, setDoc] = useState<ISasPropostaOrdemCompraDocs>({} as ISasPropostaOrdemCompraDocs);

    useEffect(() => {
        var doc = vendorListDocs.find(f => f.id == sasProposta.id);
        if (isObjNotEmpty(doc)) {
            setDoc(doc || {} as ISasPropostaOrdemCompraDocs);
        }
    }, [exibirModal]);

    const handleCancel = () => {
        setExibirModal(false);
    };

    const handleCancelAndClose = () => {
        setExibirModal(false);
        setModalAcoesId('');
    };

    const handleSubmit = (values: any) => {
        const form = vtObjToFormData(values);


        if (editando.sasPropostaId == sasProposta.id) {
            Modal.confirm({
                title: <>{editando.justificativa}</>,
                content: <ModalConfirmContentJustificativa
                    texto='É necessário informar a justificativa para a alteração.'
                    setJustificativa={e => {
                        form.set('justificativaEdicao', e);
                    }}
                />,
                okText: 'Confirmar',
                cancelText: 'Cancelar',
                onOk: async (e) => {
                    form.append('editando', 'true');
                    return new Promise((resolve, reject) => {
                        anexarRelatorioConfide(sasProposta.id, form)
                            .then((data: any) => {
                                if (data && data.valid) {
                                    return resolve(true);
                                } else {
                                    if (data.notifications && data.notifications.length > 0) {
                                        const erroJustificativaEdicao = data.notifications.findIndex((c: any) => {
                                            return c.property == 'justificativaEdicao';
                                        });
                                        if (erroJustificativaEdicao >= 0) {
                                            return reject(false);
                                        }
                                    }
                                }
                            })
                            .finally(handleCancelAndClose);
                    });
                },
                onCancel: handleCancelAndClose
            });
        } else {
            anexarRelatorioConfide(sasProposta.id, form)
                .then((data: any) => {
                    if (data && data.valid) {
                        handleCancelAndClose();
                    }
                });
        }
    };

    return (
        <>
            <Tooltip title="Anexar Relatório Financeiro">
                <Button icon='business-set_as_resume' onClick={() => { setExibirModal(true); }} />
            </Tooltip>

            <Visibility visible={isObjNotEmpty(sasProposta) && exibirModal} >
                <MasterDetailForm
                    key={`formEnvioConfide_${sasProposta.id}`}
                    validationSchema={
                        Yup.object().shape({
                            arquivo: isObjNotEmpty(doc) && doc.anexoRelatorioConfide ? Yup.mixed().notRequired() : Yup.mixed().required('É necessário anexar um arquivo.'),
                        })
                    }
                    initialValues={sasProposta}
                >
                    <Modal
                        title={`Anexar Relatório Financeiro`}
                        width='780px'
                        visible={exibirModal}
                        onCancel={handleCancel}
                        footer={[
                            <Button title='fechar' type='link' onClick={() => handleCancel()} />,
                            <FormSubmitButton title='Salvar' type='primary' noIcon onClick={handleSubmit} />
                        ]}
                    >
                        <Row>
                            <FormSelect
                                label='Status'
                                name='statusRelatorioConfide'
                                value={sasProposta.statusRelatorioConfide
                                    ? sasProposta.statusRelatorioConfide
                                    : EStatusRelatorioConfide.PENDENTE_RECEBIMENTO
                                }
                                options={[
                                    { text: EStatusRelatorioConfide.PENDENTE_RECEBIMENTO, value: EStatusRelatorioConfide.PENDENTE_RECEBIMENTO },
                                    { text: EStatusRelatorioConfide.ACOMPANHAMENTO_DO_PROCESSO, value: EStatusRelatorioConfide.ACOMPANHAMENTO_DO_PROCESSO },
                                    { text: EStatusRelatorioConfide.RECEBIDO, value: EStatusRelatorioConfide.RECEBIDO },
                                ]}
                                span={6}
                                style={{ width: '100%' }}
                                disabled
                            />
                            <FormDatePicker
                                format='DD/MM/YYYY'
                                name='dataStatusRelatorioConfide'
                                label='Data de Envio'
                                required
                                span={6} />
                        </Row>
                        <Row>
                            <UploadFile
                                idDoComponente='arquivos-ModalStatusRelatorioConfide'
                                type='primary'
                                hasFile={false}
                                hasLabel={true}
                                icon='ui-upload'
                                span={6}
                                onGetArquivo={() => {

                                    if (isObjNotEmpty(doc.anexoRelatorioConfide)) {
                                        return Promise.resolve({
                                            id: doc.anexoRelatorioConfide.id,
                                            nome: doc.anexoRelatorioConfide.nome,
                                            status: "",
                                            statusArquivoId: doc.anexoRelatorioConfide.statusArquivoId || "",
                                        } as IArquivo);
                                    }
                                    return Promise.resolve(undefined);
                                }}
                                showDownload={true}
                            />
                        </Row>
                    </Modal>
                </MasterDetailForm>
            </Visibility>
        </>
    );
};